/**
 * Ability charges — serial charge recovery for abilities like Lanternskip
 * (capacity 2, serial 5s recharge, 380ms recast floor).
 *
 * Distinct from recast chains (recastChains.ts):
 *   - Recast chains grant rapid consecutive presses inside an open window, then
 *     trigger full cooldown all at once.
 *   - Ability charges track individual discrete charges (e.g. 2 max).
 *   - Recovery is strictly serial: only one charge recharges at a time.
 *     Spending at t=0 recovers at t=5000. Spending again at t=500 recovers at
 *     t=10000 (the second charge starts its 5000ms recovery when the first finishes).
 *   - A minimum recast floor (380ms for Lanternskip) prevents instant double-tap
 *     without modifying or incurring the standard 500ms combat GCD.
 *
 * Shared by client (CooldownStore/PlayerManagerAbilityActions) and server
 * (CombatCastHandler/DashHandler).
 */

export interface AbilityChargeConfig {
  /** Maximum number of charges held (e.g. 2). */
  maxCharges: number;
  /** Milliseconds to recharge one spent charge (e.g. 5000ms). */
  rechargeTimeMs: number;
  /** Charges consumed per cast (default 1). */
  consumePerCast?: number;
  /** Minimum interval between consecutive casts in ms (e.g. 380ms for Lanternskip). */
  minRecastIntervalMs?: number;
}

export interface AbilityChargeState {
  currentCharges: number;
  maxCharges: number;
  rechargeTimeMs: number;
  /** Timestamp (epoch ms) when the current recovering charge started recharging. null if full. */
  rechargeStartedAt: number | null;
  /** Timestamp (epoch ms) of the most recent cast, or null if never cast. */
  lastCastAt: number | null;
}

export const ABILITY_CHARGE_CONFIGS: Readonly<Record<string, AbilityChargeConfig>> = Object.freeze({
  lanternskip: Object.freeze({
    maxCharges: 2,
    rechargeTimeMs: 5000,
    consumePerCast: 1,
    minRecastIntervalMs: 380,
  }),
});

export function abilityChargeConfig(abilityId: string): AbilityChargeConfig | undefined {
  return ABILITY_CHARGE_CONFIGS[abilityId];
}

/**
 * Synchronize serial charge state forward to `now`.
 * Pure state mutation on `state`.
 */
export function syncChargeState(state: AbilityChargeState, now: number): void {
  if (state.currentCharges >= state.maxCharges) {
    state.currentCharges = state.maxCharges;
    state.rechargeStartedAt = null;
    return;
  }

  if (state.rechargeStartedAt === null) {
    // Missing charges but timer wasn't running; start now
    state.rechargeStartedAt = now;
    return;
  }

  const elapsed = now - state.rechargeStartedAt;
  if (elapsed >= state.rechargeTimeMs) {
    const recovered = Math.floor(elapsed / state.rechargeTimeMs);
    const newCharges = Math.min(state.maxCharges, state.currentCharges + recovered);
    if (newCharges >= state.maxCharges) {
      state.currentCharges = state.maxCharges;
      state.rechargeStartedAt = null;
    } else {
      state.currentCharges = newCharges;
      // Serial recovery: remainder carries over to next charge
      state.rechargeStartedAt += recovered * state.rechargeTimeMs;
    }
  }
}

/**
 * Check whether a charge can be spent at `now`.
 */
export function checkChargeSpend(
  state: AbilityChargeState,
  config: AbilityChargeConfig,
  now: number,
): { ok: boolean; reason?: 'no_charges' | 'recast_gate'; remainingMs?: number } {
  syncChargeState(state, now);
  const cost = config.consumePerCast ?? 1;

  if (state.currentCharges < cost) {
    const elapsed = state.rechargeStartedAt !== null ? now - state.rechargeStartedAt : 0;
    const remainingMs = Math.max(0, state.rechargeTimeMs - (elapsed % state.rechargeTimeMs));
    return { ok: false, reason: 'no_charges', remainingMs };
  }

  if (config.minRecastIntervalMs && state.lastCastAt !== null) {
    const elapsedSinceLastCast = now - state.lastCastAt;
    if (elapsedSinceLastCast < config.minRecastIntervalMs) {
      return {
        ok: false,
        reason: 'recast_gate',
        remainingMs: config.minRecastIntervalMs - elapsedSinceLastCast,
      };
    }
  }

  return { ok: true };
}

/**
 * Spend a charge from `state` at `now`. Returns true if successfully consumed.
 */
export function spendCharge(
  state: AbilityChargeState,
  config: AbilityChargeConfig,
  now: number,
): boolean {
  const verdict = checkChargeSpend(state, config, now);
  if (!verdict.ok) return false;

  const cost = config.consumePerCast ?? 1;
  state.currentCharges -= cost;
  state.lastCastAt = now;

  // If we were at capacity before this spend, begin recharge timer for this charge
  if (state.rechargeStartedAt === null) {
    state.rechargeStartedAt = now;
  }
  // Otherwise, existing serial recharge timer continues ticking uninterrupted!

  return true;
}

/**
 * Refund a charge to `state` (e.g. wall collision < 24px or cast rejection).
 */
export function refundCharge(
  state: AbilityChargeState,
  config: AbilityChargeConfig,
  now: number,
): boolean {
  syncChargeState(state, now);
  const cost = config.consumePerCast ?? 1;
  if (state.currentCharges >= state.maxCharges) return false;

  state.currentCharges = Math.min(state.maxCharges, state.currentCharges + cost);
  if (state.currentCharges >= state.maxCharges) {
    state.rechargeStartedAt = null;
  }
  return true;
}

/**
 * Create an initial full-charge state for an ability config.
 */
export function createInitialChargeState(config: AbilityChargeConfig): AbilityChargeState {
  return {
    currentCharges: config.maxCharges,
    maxCharges: config.maxCharges,
    rechargeTimeMs: config.rechargeTimeMs,
    rechargeStartedAt: null,
    lastCastAt: null,
  };
}

/**
 * Keyed charge tracker used by server sessions or client singletons.
 */
interface KeyWithChargeState {
  /** Persistent character identity on the live Player schema (R2-04). */
  dbId?: string;
  characterId?: string;
  abilityCharges?: Map<string, AbilityChargeState>;
}

export class AbilityChargeTracker<TKey extends object> {
  private states = new WeakMap<TKey, Map<string, AbilityChargeState>>();
  private characterStates = new Map<string, Map<string, AbilityChargeState>>();

  private getPlayerMap(key: TKey): Map<string, AbilityChargeState> {
    const anyKey = key as KeyWithChargeState;
    // The live Player schema identifies its character by dbId; characterId is
    // retained for older fixtures. A new Player object for the same character
    // shares the cached state instead of refilling (R2-04).
    const charId = anyKey?.dbId || anyKey?.characterId;
    if (charId && typeof charId === 'string') {
      let charMap = this.characterStates.get(charId);
      if (!charMap) {
        charMap = (anyKey.abilityCharges instanceof Map) ? anyKey.abilityCharges : new Map<string, AbilityChargeState>();
        this.characterStates.set(charId, charMap!);
      }
      if (anyKey && typeof anyKey === 'object') {
        anyKey.abilityCharges = charMap;
      }
      return charMap!;
    }

    if (anyKey && anyKey.abilityCharges instanceof Map) {
      return anyKey.abilityCharges;
    }
    let map = this.states.get(key);
    if (!map) {
      map = new Map();
      this.states.set(key, map);
      if (anyKey && typeof anyKey === 'object') {
        anyKey.abilityCharges = map;
      }
    }
    return map;
  }

  clear(): void {
    this.characterStates.clear();
  }

  getOrCreate(key: TKey, abilityId: string, now: number = Date.now()): AbilityChargeState | undefined {
    const config = abilityChargeConfig(abilityId);
    if (!config) return undefined;

    const map = this.getPlayerMap(key);
    let state = map.get(abilityId);
    if (!state) {
      state = createInitialChargeState(config);
      map.set(abilityId, state);
    } else {
      syncChargeState(state, now);
    }
    return state;
  }

  canSpend(
    key: TKey,
    abilityId: string,
    now: number = Date.now(),
  ): { ok: boolean; reason?: 'no_charges' | 'recast_gate'; remainingMs?: number } {
    const config = abilityChargeConfig(abilityId);
    if (!config) return { ok: true };
    const state = this.getOrCreate(key, abilityId, now);
    if (!state) return { ok: false, reason: 'no_charges' };
    return checkChargeSpend(state, config, now);
  }

  spend(key: TKey, abilityId: string, now: number = Date.now()): boolean {
    const config = abilityChargeConfig(abilityId);
    if (!config) return true;
    const state = this.getOrCreate(key, abilityId, now);
    if (!state) return false;
    return spendCharge(state, config, now);
  }

  refund(key: TKey, abilityId: string, now: number = Date.now()): boolean {
    const config = abilityChargeConfig(abilityId);
    if (!config) return false;
    const state = this.getOrCreate(key, abilityId, now);
    if (!state) return false;
    return refundCharge(state, config, now);
  }

  getCharges(key: TKey, abilityId: string, now: number = Date.now()): number {
    const state = this.getOrCreate(key, abilityId, now);
    return state ? state.currentCharges : 0;
  }

  getRechargeStartedAt(key: TKey, abilityId: string, now: number = Date.now()): number | null {
    const state = this.getOrCreate(key, abilityId, now);
    return state ? state.rechargeStartedAt : null;
  }

  getRemainingRechargeMs(key: TKey, abilityId: string, now: number = Date.now()): number {
    const state = this.getOrCreate(key, abilityId, now);
    if (!state || state.currentCharges >= state.maxCharges || state.rechargeStartedAt === null) {
      return 0;
    }
    const elapsed = now - state.rechargeStartedAt;
    return Math.max(0, state.rechargeTimeMs - (elapsed % state.rechargeTimeMs));
  }
}
